class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    this.ensureCartDrawerEnhancements();

    if (this.classList.contains('active')) return;

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  ensureCartDrawerEnhancements() {
    if (!document.getElementById('actively-cart-drawer-style')) {
      const style = document.createElement('style');
      style.id = 'actively-cart-drawer-style';
      style.textContent = `
        .drawer { background-color: rgba(0,0,0,.48); transition: visibility .45s ease; }
        .drawer__inner { background: rgb(var(--color-background)); box-shadow: -10px 0 30px rgba(0,0,0,.18); transition: transform .45s cubic-bezier(.22,1,.36,1); }
        .drawer__header { min-height: 7rem; padding: 1.5rem 0; border-bottom: 1px solid rgba(var(--color-foreground),.12); }
        .drawer__heading { margin: 0; font-size: 2rem; font-weight: 700; letter-spacing: .02em; text-transform: uppercase; }
        .drawer__close { right: 0; top: 50%; transform: translateY(-50%); }
        .cart-drawer .cart-item { padding: 1.35rem 0; margin: 0; border-bottom: 1px solid rgba(var(--color-foreground),.12); }
        .cart-drawer .cart-item__media { width: 10rem; }
        .cart-drawer .cart-item__image { width: 100%; border-radius: .8rem; object-fit: cover; }
        .cart-drawer .cart-item__name { font-size: 1.6rem; line-height: 1.3; }
        .cart-drawer .cart-item__details .product-option { margin-top: .4rem; font-size: 1.35rem; }
        .cart-drawer .cart-item__quantity .quantity-popover-wrapper { display:flex; align-items:center; gap:1rem; }
        .cart-drawer .cart-item__quantity .quantity { min-height:4.2rem; border:2px solid #111111 !important; border-radius:999px; overflow:hidden; box-sizing:border-box; box-shadow:none !important; }
        .cart-drawer .cart-item__quantity .quantity::before,
        .cart-drawer .cart-item__quantity .quantity::after { display:none !important; content:none !important; }
        .cart-drawer .cart-item__quantity .quantity__input { font-weight:800 !important; opacity:1 !important; color:#111111 !important; }
        .cart-drawer .cart-item__quantity .quantity__button { width:4rem; color:#111111; }
        .cart-drawer .cart-remove-button { min-width:4rem; min-height:4rem; }
        .cart-drawer__delivery-row { display:flex; justify-content:space-between; align-items:center; gap:1rem; padding:1.15rem 1.25rem; margin:1rem 0 1.2rem; border-radius:1rem; background:rgba(var(--color-button),.06); font-size:1.45rem; }
        .cart-drawer__delivery-row strong { color:rgb(var(--color-button)); font-weight:800; }
        .cart-drawer .totals { padding-top:1rem; border-top:1px solid rgba(var(--color-foreground),.12); }
        .cart-drawer .totals__total { font-size:1.65rem; font-weight:700; }
        .cart-drawer .totals__total-value { font-size:1.8rem; font-weight:800; }
        .cart-drawer .cart__checkout-button { width:100%; max-width:none; min-height:5.2rem; border-radius:999px; font-weight:800; font-size:1.65rem !important; line-height:1.2; background:#436858!important; color:#000000!important; border:0!important; --buttons-border-width:0px!important; --buttons-border-opacity:0!important; --buttons-shadow-opacity:0!important; box-shadow:0 5px 0 #2f4d41, 0 10px 18px rgba(0,0,0,.20)!important; animation: buyNowVibrate-cart-drawer 3.5s ease-in-out infinite; transform-origin:center; }
        .cart-drawer .cart__checkout-button::before,
        .cart-drawer .cart__checkout-button::after { display:none !important; content:none !important; box-shadow:none !important; border:0 !important; }
        @keyframes buyNowVibrate-cart-drawer {
          0%, 90%, 100% { transform: translateX(0) rotate(0); }
          91% { transform: translateX(-3px) rotate(-1deg); }
          92% { transform: translateX(3px) rotate(1deg); }
          93% { transform: translateX(-3px) rotate(-1deg); }
          94% { transform: translateX(3px) rotate(1deg); }
          95% { transform: translateX(-2px) rotate(0); }
          96% { transform: translateX(2px) rotate(0); }
          97%, 98%, 99% { transform: translateX(0) rotate(0); }
        }
        .cart-drawer .cart__checkout-button:hover { background:#4d7563!important; color:#000000!important; box-shadow:0 6px 0 #365b4c, 0 12px 20px rgba(0,0,0,.22)!important; }
        .cart-drawer .cart__checkout-button:active { box-shadow:0 2px 0 #2f4d41, 0 4px 8px rgba(0,0,0,.16)!important; }
        @media (prefers-reduced-motion: reduce) { .cart-drawer .cart__checkout-button { animation:none; } }
        @media screen and (max-width:749px) {
          .drawer__inner { width:100vw; max-width:100vw; padding:0 1.4rem; }
          .drawer__header { min-height:6.2rem; }
          .drawer__heading { font-size:1.75rem; }
          .cart-drawer .cart-item { grid-template-columns:8.5rem 1fr; gap:1rem; }
          .cart-drawer .cart-item__media { width:auto; grid-row:1 / 3; }
          .cart-drawer .cart-item__details { grid-column:2; }
          .cart-drawer .cart-item__totals { grid-column:2; grid-row:1; justify-content:flex-end; }
          .cart-drawer .cart-item__quantity { grid-column:2; }
          .cart-drawer .cart-item__name { font-size:1.45rem; }
          .cart-drawer .cart-item__quantity .quantity { min-height:3.8rem; }
        }
      `;
      document.head.appendChild(style);
    }

    const footer = this.querySelector('.cart-drawer__footer');
    const totals = footer?.querySelector('.totals');
    if (footer && totals && !footer.querySelector('.cart-drawer__delivery-row')) {
      const delivery = document.createElement('div');
      delivery.className = 'cart-drawer__delivery-row';
      delivery.innerHTML = '<span>Delivery</span><strong>FREE</strong>';
      totals.insertAdjacentElement('afterend', delivery);
    }
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');
    if (cartDrawerNote.nextElementSibling.getAttribute('id')) cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    cartDrawerNote.addEventListener('click', (event) => event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open')));
    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner?.classList.contains('is-empty')) drawerInner.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });
    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [{ id: 'cart-drawer', selector: '#CartDrawer' }, { id: 'cart-icon-bubble' }];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
